import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { I18nProvider } from "@/i18n/i18n";
import { NavigationGuardProvider } from "@/context/NavigationGuard";
import { ConfirmProvider } from "@/hooks/useConfirm";
import { Toaster } from "@/components/ui/sonner";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import AuthMagic from "@/pages/AuthMagic";
import DashboardLayout from "@/components/layout/DashboardLayout";
import RoleHome from "@/pages/dashboard/RoleHome";
import Sedes from "@/pages/dashboard/Sedes";
import Programs from "@/pages/dashboard/Programs";
import UsersPage from "@/pages/dashboard/Users";
import Groups from "@/pages/dashboard/Groups";
import Payments from "@/pages/dashboard/Payments";
import Expenses from "@/pages/dashboard/Expenses";
import Resources from "@/pages/dashboard/Resources";
import Notebooks from "@/pages/dashboard/Notebooks";
import StudyPlans from "@/pages/dashboard/StudyPlans";
import ClassPlan from "@/pages/dashboard/ClassPlan";
import MyLevelPlan from "@/pages/student/MyLevelPlan";
import Wall from "@/pages/dashboard/Wall";
import Gallery from "@/pages/dashboard/Gallery";
import Announcements from "@/pages/dashboard/Announcements";
import Alerts from "@/pages/dashboard/Alerts";
import Reports from "@/pages/dashboard/Reports";
import CalendarMonth from "@/pages/dashboard/CalendarMonth";
import TeacherAttendance from "@/pages/teacher/Attendance";
import TeacherJournal from "@/pages/teacher/Journal";
import TeacherHomework from "@/pages/teacher/Homework";
import MyAttendance from "@/pages/student/MyAttendance";
import Achievements from "@/pages/student/Achievements";
import WallBoard from "@/pages/dashboard/WallBoard";
import MyPayments from "@/pages/student/MyPayments";
import MyJournal from "@/pages/student/MyJournal";
import MyNotebook from "@/pages/student/MyNotebook";
import MyHomework from "@/pages/student/MyHomework";
import Onboarding from "@/pages/Onboarding";
import AcceptPolicies from "@/pages/AcceptPolicies";
import ChurnFeedback from "@/pages/ChurnFeedback";
import ChurnFeedbacks from "@/pages/dashboard/ChurnFeedbacks";
import Profile from "@/pages/dashboard/Profile";
import Notifications from "@/pages/dashboard/Notifications";
import InvoiceSettings from "@/pages/dashboard/InvoiceSettings";
import MailCenterPage from "@/pages/dashboard/MailCenterPage";
import Help from "@/pages/dashboard/Help";
import MyAbsences from "@/pages/student/MyAbsences";
import AbsenceRequestsAdmin from "@/pages/dashboard/AbsenceRequestsAdmin";
import Leads from "@/pages/dashboard/Leads";
import LeadsAnalytics from "@/pages/dashboard/LeadsAnalytics";
import LeadsEffectivenessPreview from "@/pages/dashboard/LeadsEffectivenessPreview";
import LivePassHomePreview from "@/pages/dashboard/LivePassHomePreview";
import GameZonePreview from "@/pages/dashboard/GameZonePreview";
import GameZone from "@/pages/dashboard/GameZone";
import LeadTemplates from "@/pages/dashboard/LeadTemplates";
import LeadAutomation from "@/pages/dashboard/LeadAutomation";
import Distribution from "@/pages/dashboard/Distribution";
import Affiliates from "@/pages/dashboard/Affiliates";
import Courses from "@/pages/dashboard/Courses";
import CourseEditor from "@/pages/dashboard/CourseEditor";
import CourseSales from "@/pages/public/CourseSales";
import CourseCatalog from "@/pages/public/CourseCatalog";
import CertificateVerify from "@/pages/public/CertificateVerify";
import BookingPage from "@/pages/public/BookingPage";
import Amigos from "@/pages/public/Amigos";
import AmigosPractice from "@/pages/student/AmigosPractice";
import BookingManage from "@/pages/public/BookingManage";
import BookingSettings from "@/pages/dashboard/Booking";
import MyCourses from "@/pages/student/MyCourses";
import CoursePlayer from "@/pages/student/CoursePlayer";
import LiveSessions from "@/pages/teacher/LiveSessions";
import LiveClasses from "@/pages/student/LiveClasses";
import Conjugation from "@/pages/student/Conjugation";
import Messages from "@/pages/Messages";
import Packages from "@/pages/public/Packages";
import LivePublic from "@/pages/public/LivePublic";
import LiveEmbed from "@/pages/public/LiveEmbed";
import GoLanding from "@/pages/public/GoLanding";
import LivePassAdmin from "@/pages/dashboard/LivePass";
import AttendanceGrid from "@/pages/dashboard/AttendanceGrid";
import StoreOrders from "@/pages/dashboard/StoreOrders";
import { Loader2 } from "lucide-react";

function needsOnboarding(user) {
  return !!user && (user.role === "student" || user.role === "teacher") && user.profile_completed === false;
}

// Existing users (already onboarded) that haven't accepted the CURRENT Policies
// & Conditions version yet — they get redirected to a lightweight acceptance
// page. This intentionally triggers a re-acceptance whenever the backend bumps
// CURRENT_POLICIES_VERSION (i.e. when policies are updated).
function needsPoliciesAcceptance(user, currentVersion) {
  if (!user) return false;
  if (user.role !== "student" && user.role !== "teacher") return false;
  if (user.profile_completed === false) return false; // will be handled by onboarding
  if (!currentVersion) {
    // Backend version not loaded yet -> fall back to "has ever accepted?"
    return !user.policies_accepted_at;
  }
  return user.policies_version !== currentVersion;
}

function Protected({ children, roles }) {
  const { user, loading, setUser, policies } = useAuth();
  const location = useLocation();
  // Defensive fallback: AuthCallback passes the user via navigate state to avoid a
  // race where setUser hasn't committed before Protected reads context.
  const stateUser = location.state?.user;
  const effectiveUser = user || stateUser;

  // Sync the state-passed user into the AuthContext so subsequent renders are stable
  useEffect(() => {
    if (!user && stateUser) {
      setUser(stateUser);
    }
  }, [user, stateUser, setUser]);

  if (loading && !effectiveUser) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-7 w-7 animate-spin text-blue-600" />
      </div>
    );
  }
  if (!effectiveUser) return <Navigate to="/login" replace />;
  if (effectiveUser.approval_status === "rejected") return <Navigate to="/login" replace />;
  if (needsOnboarding(effectiveUser)) return <Navigate to="/onboarding" replace />;
  if (needsPoliciesAcceptance(effectiveUser, policies?.version)) return <Navigate to="/accept-policies" replace />;
  if (roles && !roles.includes(effectiveUser.role)) return <Navigate to="/dashboard" replace />;
  return children;
}

function AcceptPoliciesRoute() {
  const { user, loading, policies } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-7 w-7 animate-spin text-blue-600" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (needsOnboarding(user)) return <Navigate to="/onboarding" replace />;
  if (!needsPoliciesAcceptance(user, policies?.version)) return <Navigate to="/dashboard" replace />;
  return <AcceptPolicies />;
}

function OnboardingRoute() {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-7 w-7 animate-spin text-blue-600" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (!needsOnboarding(user)) return <Navigate to="/dashboard" replace />;
  return <Onboarding />;
}

function AppRouter() {
  const location = useLocation();
  // CRITICAL: Detect session_id in URL fragment synchronously during render
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="/login" element={<Login />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/auth/magic" element={<AuthMagic />} />
      <Route path="/feedback/:token" element={<ChurnFeedback />} />
      <Route path="/cursos" element={<CourseCatalog />} />
      <Route path="/cursos/:courseId" element={<CourseSales />} />
      <Route path="/certificado/:code" element={<CertificateVerify />} />
      <Route path="/amigos" element={<Amigos />} />
      <Route path="/reservar/gestion/:token" element={<BookingManage />} />
      <Route path="/reservar/:slug" element={<BookingPage />} />
      <Route path="/paquetes" element={<Packages />} />
      <Route path="/wss-live" element={<LivePublic />} />
      <Route path="/go" element={<GoLanding />} />
      <Route path="/wss-go" element={<GoLanding />} />
      <Route path="/wss-live/embed" element={<LiveEmbed />} />
      <Route path="/onboarding" element={<OnboardingRoute />} />
      <Route path="/accept-policies" element={<AcceptPoliciesRoute />} />
      <Route
        path="/dashboard"
        element={
          <Protected>
            <DashboardLayout />
          </Protected>
        }
      >
        <Route index element={<RoleHome />} />
        <Route path="sedes" element={<Protected roles={["superadmin","academic_director"]}><Sedes /></Protected>} />
        <Route path="programs" element={<Protected roles={["superadmin","academic_director"]}><Programs /></Protected>} />
        <Route path="users" element={<Protected roles={["superadmin"]}><UsersPage /></Protected>} />
        <Route path="teachers" element={<Protected roles={["superadmin","academic_director"]}><UsersPage filterRole="teacher" /></Protected>} />
        <Route path="students" element={<Protected roles={["superadmin","academic_director","teacher"]}><UsersPage filterRole="student" /></Protected>} />
        <Route path="groups" element={<Protected roles={["superadmin","academic_director"]}><Groups mode="group" /></Protected>} />
        <Route path="private" element={<Protected roles={["superadmin","academic_director"]}><Groups mode="private" /></Protected>} />
        <Route path="payments" element={<Protected roles={["superadmin"]}><Payments /></Protected>} />
        <Route path="expenses" element={<Protected roles={["superadmin"]}><Expenses /></Protected>} />
        <Route path="resources" element={<Protected roles={["superadmin","academic_director","teacher","student"]}><Resources /></Protected>} />
        <Route path="notebooks" element={<Protected roles={["superadmin","academic_director","teacher"]}><Notebooks /></Protected>} />
        <Route path="study-plans" element={<Protected roles={["superadmin","academic_director"]}><StudyPlans /></Protected>} />
        <Route path="class-plan" element={<Protected roles={["superadmin","academic_director","teacher"]}><ClassPlan /></Protected>} />
        <Route path="my-plan" element={<Protected roles={["student"]}><MyLevelPlan /></Protected>} />
        <Route path="wall" element={<Protected roles={["superadmin","academic_director"]}><Wall /></Protected>} />
        <Route path="amigos" element={<Protected roles={["superadmin","academic_director","teacher","student"]}><AmigosPractice /></Protected>} />
        <Route path="gallery" element={<Protected roles={["superadmin","academic_director","teacher","student"]}><Gallery /></Protected>} />
        <Route path="booking" element={<Protected roles={["superadmin","academic_director"]}><BookingSettings /></Protected>} />
        <Route path="announcements" element={<Protected roles={["superadmin","academic_director"]}><Announcements /></Protected>} />
        <Route path="alerts" element={<Protected roles={["superadmin","academic_director"]}><Alerts /></Protected>} />
        <Route path="churn" element={<Protected roles={["superadmin","academic_director"]}><ChurnFeedbacks /></Protected>} />
        <Route path="invoice-settings" element={<Protected roles={["superadmin"]}><InvoiceSettings /></Protected>} />
        <Route path="mail-center" element={<Protected roles={["superadmin"]}><MailCenterPage /></Protected>} />
        <Route path="profile" element={<Profile />} />
        <Route path="help" element={<Help />} />
        <Route path="my-absences" element={<MyAbsences />} />
        <Route path="absence-requests" element={<Protected roles={["superadmin","academic_director"]}><AbsenceRequestsAdmin /></Protected>} />
        <Route path="leads" element={<Protected roles={["superadmin","admissions_lead_coordinator"]}><Leads /></Protected>} />
        <Route path="leads/analytics" element={<Protected roles={["superadmin","admissions_lead_coordinator"]}><LeadsAnalytics /></Protected>} />
        <Route path="leads/efectividad-preview" element={<Protected roles={["superadmin","admissions_lead_coordinator"]}><LeadsEffectivenessPreview /></Protected>} />
        <Route path="panel-pase-preview" element={<Protected roles={["superadmin"]}><LivePassHomePreview /></Protected>} />
        <Route path="game-zone-preview" element={<Protected roles={["superadmin"]}><GameZonePreview /></Protected>} />
        <Route path="game-zone" element={<Protected roles={["superadmin","academic_director","teacher","student"]}><GameZone /></Protected>} />
        <Route path="lead-templates" element={<Protected roles={["superadmin","admissions_lead_coordinator"]}><LeadTemplates /></Protected>} />
        <Route path="lead-automation" element={<Protected roles={["superadmin","admissions_lead_coordinator"]}><LeadAutomation /></Protected>} />
        <Route path="distribution" element={<Protected roles={["superadmin","admissions_lead_coordinator"]}><Distribution /></Protected>} />
        <Route path="affiliates" element={<Protected roles={["superadmin","academic_director"]}><Affiliates /></Protected>} />
        <Route path="pedidos" element={<Protected roles={["superadmin"]}><StoreOrders /></Protected>} />
        <Route path="courses" element={<Protected roles={["superadmin","academic_director"]}><Courses /></Protected>} />
        <Route path="courses/:courseId" element={<Protected roles={["superadmin","academic_director"]}><CourseEditor /></Protected>} />
        <Route path="my-courses" element={<MyCourses />} />
        <Route path="live-sessions" element={<Protected roles={["superadmin","academic_director","teacher"]}><LiveSessions /></Protected>} />
        <Route path="live-pass" element={<Protected roles={["superadmin"]}><LivePassAdmin /></Protected>} />
        <Route path="attendance-report" element={<Protected roles={["superadmin", "academic_director"]}><AttendanceGrid /></Protected>} />
        <Route path="live-classes" element={<Protected roles={["student"]}><LiveClasses /></Protected>} />
        <Route path="conjugation" element={<Protected roles={["student","teacher","academic_director","superadmin"]}><Conjugation /></Protected>} />
        <Route path="my-courses/:courseId" element={<CoursePlayer />} />
        <Route path="notifications" element={<Notifications />} />
        <Route path="reports" element={<Protected roles={["superadmin"]}><Reports /></Protected>} />
        <Route path="attendance" element={<Protected roles={["superadmin","teacher","academic_director"]}><TeacherAttendance /></Protected>} />
        <Route path="journal" element={<Protected roles={["superadmin","teacher","academic_director"]}><TeacherJournal /></Protected>} />
        <Route path="homework" element={<Protected roles={["superadmin","teacher","academic_director"]}><TeacherHomework /></Protected>} />
        <Route path="messages" element={<Protected roles={["superadmin","academic_director","teacher","student","affiliate"]}><Messages /></Protected>} />
        <Route path="calendar" element={<Protected roles={["superadmin","teacher","academic_director","student"]}><CalendarMonth /></Protected>} />
        <Route path="my-attendance" element={<Protected roles={["student"]}><MyAttendance /></Protected>} />
        <Route path="achievements" element={<Protected roles={["student"]}><Achievements /></Protected>} />
        <Route path="wall-board" element={<Protected roles={["student","teacher"]}><WallBoard /></Protected>} />
        <Route path="my-payments" element={<Protected roles={["student"]}><MyPayments /></Protected>} />
        <Route path="my-journal" element={<Protected roles={["student"]}><MyJournal /></Protected>} />
        <Route path="my-notebook" element={<Protected roles={["student"]}><MyNotebook /></Protected>} />
        <Route path="my-homework" element={<Protected roles={["student"]}><MyHomework /></Protected>} />
      </Route>
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

export default function App() {
  useEffect(() => {
    document.title = "We Speak Spanish — Plataforma";
  }, []);
  return (
    <I18nProvider>
      <AuthProvider>
        <NavigationGuardProvider>
          <ConfirmProvider>
            <BrowserRouter>
              <AppRouter />
              <Toaster richColors position="top-right" />
            </BrowserRouter>
          </ConfirmProvider>
        </NavigationGuardProvider>
      </AuthProvider>
    </I18nProvider>
  );
}
